import React, { useState, useEffect, useMemo, useRef, useCallback } from 'react';


/* ─────────────────────────────────────────────────────────────
   ERPForce PDF Builder — master-template builder helpers
   The artifact authors build is a TEMPLATE (HTML + {{placeholders}}).
   Structure is config-driven: line-item table columns and document
   meta fields are arrays the author edits; we generate the template
   HTML from them and splice it into the base template. The same
   template renders server-side at use time. Exposes PBTpl.
   ───────────────────────────────────────────────────────────── */

const esc = (s) => String(s == null ? '' : s).replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;');

// ── catalog: line-item fields a column can bind to ───────────
const ITEM_FIELDS = [
  { id: 'index', label: 'Row number', expr: '{{@number}}' },
  { id: 'name', label: 'Description (+ SKU)', expr: '{{this.name}}' },
  { id: 'sku', label: 'SKU / code', expr: '{{this.sku}}' },
  { id: 'detail', label: 'Sub-detail', expr: '{{this.detail}}' },
  { id: 'qty', label: 'Quantity', expr: '{{qty this.qty}}' },
  { id: 'unit', label: 'Unit', expr: '{{this.unit}}' },
  { id: 'price', label: 'Unit price', expr: '{{money this.price}}' },
  { id: 'disc', label: 'Discount %', expr: '{{pct this.disc}}' },
  { id: 'tax', label: 'Tax / VAT %', expr: '{{pct this.tax}}' },
  { id: 'amount', label: 'Line amount', expr: '{{money this.amount}}' },
];
const itemFieldLabel = (id) => (ITEM_FIELDS.find(f => f.id === id) || { label: id }).label;

// ── catalog: document-level meta placeholders (invoice) ──────
const META_CATALOG = [
  { field: 'doc.number', label: 'Invoice no.', expr: '{{doc.number}}' },
  { field: 'doc.date', label: 'Issue date', expr: '{{doc.date}}' },
  { field: 'doc.dueDate', label: 'Due date', expr: '{{doc.dueDate}}' },
  { field: 'doc.po', label: 'PO number', expr: '{{doc.po}}' },
  { field: 'doc.terms', label: 'Payment terms', expr: '{{doc.terms}}' },
  { field: 'doc.status', label: 'Status', expr: '{{upper doc.status}}' },
  { field: 'currency', label: 'Currency', expr: '{{currency}}' },
  { field: 'customer.trn', label: 'Customer TRN', expr: '{{customer.trn}}' },
  { field: 'company.trn', label: 'Our TRN', expr: '{{company.trn}}' },
  { field: 'doc.reference', label: 'Reference', expr: '{{doc.reference}}' },
];

// ── defaults for the Tax Invoice ─────────────────────────────
function defaultColumns() {
  return [
    { id: c(), field: 'index', label: '#', align: 'l' },
    { id: c(), field: 'name', label: 'Description', align: 'l' },
    { id: c(), field: 'qty', label: 'Qty', align: 'r' },
    { id: c(), field: 'unit', label: 'Unit', align: 'r' },
    { id: c(), field: 'price', label: 'Unit price', align: 'r' },
    { id: c(), field: 'disc', label: 'Disc', align: 'r' },
    { id: c(), field: 'tax', label: 'VAT', align: 'r' },
    { id: c(), field: 'amount', label: 'Amount', align: 'r' },
  ];
}
function defaultMeta() {
  return [
    { id: c(), field: 'series_number', label: 'Invoice no.', expr: '{{series_number}}' },
    { id: c(), field: 'date', label: 'Issue date', expr: '{{date}}' },
    { id: c(), field: 'due_date', label: 'Due date', expr: '{{due_date}}' },
    { id: c(), field: 'po_number', label: 'PO number', expr: '{{po_number}}' },
  ];
}
let _id = 0; function c() { return 'k' + (++_id); }

// ── build the line-item table from a columns config ──────────
function cellFor(col) {
  const lc = col.align === 'l' ? ' l' : '';
  switch (col.field) {
    case 'index': return `<td class="l it-no">{{@number}}</td>`;
    case 'name': return `<td class="l it-desc"><b>{{this.name}}</b><span class="it-sku">{{this.sku}}</span></td>`;
    case 'sku': return `<td class="l"><span class="it-sku" style="font-size:9px">{{this.sku}}</span></td>`;
    case 'detail': return `<td class="l it-tax">{{this.description}}</td>`;
    case 'qty': return `<td class="${lc.trim()}">{{qty this.quantity}}</td>`;
    case 'unit': return `<td class="${lc.trim()}">{{this.units_of_measurement.name}}</td>`;
    case 'price': return `<td class="${lc.trim()}">{{money this.rate}}</td>`;
    case 'disc': return `<td class="it-disc${lc}">{{#if this.discount_percentage}}{{pct this.discount_percentage}}{{else}}—{{/if}}</td>`;
    case 'tax': return `<td class="it-tax${lc}">{{money this.tax_amount}}</td>`;
    case 'amount': return `<td class="${lc.trim()}"><b>{{money this.gross_amount}}</b></td>`;
    case '__custom': return `<td class="${lc.trim()}">{{this.${(col.custom || 'field').trim()}}}</td>`;
    default: {
      if (col.field.includes('amount') || col.field.includes('price') || col.field.includes('rate') || col.field.includes('tax') || col.field.includes('value')) {
        return `<td class="${lc.trim()}">{{money this.${col.field}}}</td>`;
      }
      if (col.field.includes('qty') || col.field.includes('quantity')) {
        return `<td class="${lc.trim()}">{{qty this.${col.field}}}</td>`;
      }
      if (col.field.includes('discount') || col.field.includes('pct') || col.field.includes('percentage')) {
        return `<td class="${lc.trim()}">{{pct this.${col.field}}}</td>`;
      }
      return `<td class="${lc.trim()}">{{this.${col.field}}}</td>`;
    }
  }
}
function buildItemsTable(cols, arrayKey = 'items') {
  const ths = cols.map(col => `<th class="${col.align === 'l' ? 'l' : ''}">${esc(col.label)}</th>`).join('');
  const tds = cols.map(cellFor).join('\n          ');
  return `<div class="pbd-items-wrap" data-block="items" data-label="Line items" data-split>
  <table class="pbd-items">
    <thead><tr>${ths}</tr></thead>
    <tbody>
      {{#each ${arrayKey}}}<tr>
          ${tds}
        </tr>{{/each}}
    </tbody>
  </table>
</div>`;
}

// ── build the meta strip from a fields config ────────────────
function buildMetaStrip(fields) {
  const items = fields.map(f => `<div class="pbd-meta-item"><span class="pbd-meta-k">${esc(f.label)}</span><span class="pbd-meta-v">${f.expr}</span></div>`).join('\n  ');
  return `<div class="pbd-meta" data-block="meta" data-label="Meta">
  ${items}
</div>`;
}

// ── replace a top-level <div data-block="X">…</div> ──────────
function spliceBlock(tpl, block, replacement) {
  const marker = `data-block="${block}"`;
  const at = tpl.indexOf(marker); if (at === -1) return tpl;
  const start = tpl.lastIndexOf('<', at);
  const re = /<\/?div\b[^>]*>/g; re.lastIndex = start;
  let depth = 0, m, end = -1;
  while ((m = re.exec(tpl))) {
    if (m[0][1] === '/') { depth--; if (depth === 0) { end = re.lastIndex; break; } }
    else if (!m[0].endsWith('/>')) depth++;
  }
  if (end === -1) return tpl;
  return tpl.slice(0, start) + replacement + tpl.slice(end);
}

// compose the invoice template from base + structural config
function composeInvoice(base, cols, metaFields, arrayKey = 'items') {
  let t = spliceBlock(base, 'items', buildItemsTable(cols, arrayKey));
  t = spliceBlock(t, 'meta', buildMetaStrip(metaFields));
  return t;
}

// ── placeholder lens: show {{merge fields}} as chips ─────────
// `data` carries the config booleans (showLogo + include flags). Those are
// part of the saved master, not runtime merge data, so we resolve their
// {{#if flag}} blocks here — a section you've toggled OFF must not appear in
// the placeholder view either. Everything else stays flattened to chips.
function resolveConfigBlocks(s, data) {
  const boolKeys = Object.keys(data || {}).filter(k => typeof data[k] === 'boolean');
  for (const key of boolKeys) {
    for (const tag of ['if', 'unless']) {
      const open = `{{#${tag} ${key}}}`;
      const openMark = `{{#${tag} `, closeMark = `{{/${tag}}}`;
      let idx;
      while ((idx = s.indexOf(open)) !== -1) {
        // walk to the matching close, accounting for nested same-type blocks
        let depth = 1, pos = idx + open.length;
        while (depth > 0 && pos < s.length) {
          const no = s.indexOf(openMark, pos), nc = s.indexOf(closeMark, pos);
          if (nc === -1) { pos = s.length; break; }
          if (no !== -1 && no < nc) { depth++; pos = no + openMark.length; }
          else { depth--; pos = nc + closeMark.length; }
        }
        const body = s.slice(idx + open.length, pos - closeMark.length);
        const keep = (tag === 'if') ? !!data[key] : !data[key];
        s = s.slice(0, idx) + (keep ? body : '') + s.slice(pos);
      }
    }
  }
  return s;
}

function toPlaceholderView(tpl, data) {
  let s = resolveConfigBlocks(tpl, data);
  // drop inline-edit hooks (not used in placeholder lens) so {{@index}}
  // inside their attribute values can't corrupt the markup
  s = s.replace(/\s+data-edit(-type)?="[^"]*"/g, '');
  s = s.replace(/\{\{else\}\}[\s\S]*?(\{\{\/(if|unless)\}\})/g, '$1');
  s = s.replace(/\{\{[#\/](each|if|unless)[^}]*\}\}/g, '');
  s = s.replace(/\{\{\{?([^}]+?)\}?\}\}/g, (m, inner) => {
    const tok = inner.trim().split(/\s+/); const path = tok[tok.length - 1];
    return `<span class="pbd-ph">${path.replace(/</g, '&lt;')}</span>`;
  });
  return s;
}

// ── editable placeholder lens ───────────────────────────────
// Renders the template as an editable WYSIWYG view: visible wording
// becomes editable text nodes and {{merge fields}} become editable
// token chips. Returns { html, units } where each unit records the
// exact [start,end) char range in the SOURCE template, so a single
// edit splices straight back into the template (preserving loops,
// conditionals and structure). Config-flag blocks the author turned
// off are skipped — matching what the server will emit.
function findMatchingClose(s, from, kind) {
  const openMark = `{{#${kind}`, closeMark = `{{/${kind}}}`;
  let depth = 1, pos = from;
  while (depth > 0 && pos < s.length) {
    const no = s.indexOf(openMark, pos), nc = s.indexOf(closeMark, pos);
    if (nc === -1) return s.length;
    if (no !== -1 && no < nc) { depth++; pos = no + openMark.length; }
    else { depth--; pos = nc + closeMark.length; }
  }
  return pos;
}

function toEditableTemplateView(tpl, data, labelFor) {
  data = data || {};
  const isConfigBool = (k) => typeof data[k] === 'boolean';
  const units = [];
  const stack = [];           // open blocks: { kind }
  let out = '', i = 0, litStart = -1;
  const n = tpl.length;

  const flushLit = (end) => {
    if (litStart < 0) return;
    const raw = tpl.slice(litStart, end);
    const m = raw.match(/^(\s*)([\s\S]*?)(\s*)$/);
    if (m && m[2].length) {
      const k = units.length;
      units.push({ kind: 'lit', start: litStart + m[1].length, end: end - m[3].length });
      out += m[1] + `<span class="pbd-lit" data-k="${k}">${esc(m[2])}</span>` + m[3];
    } else { out += raw; }
    litStart = -1;
  };

  while (i < n) {
    // an HTML tag — emit whole tag verbatim (mustaches inside attrs ride along)
    if (tpl[i] === '<') {
      flushLit(i);
      let j = tpl.indexOf('>', i); if (j < 0) j = n - 1;
      out += tpl.slice(i, j + 1); i = j + 1; continue;
    }
    // a handlebars construct
    if (tpl[i] === '{' && tpl[i + 1] === '{') {
      flushLit(i);
      const triple = tpl[i + 2] === '{';
      const close = triple ? '}}}' : '}}';
      let j = tpl.indexOf(close, i + (triple ? 3 : 2));
      if (j < 0) { out += tpl.slice(i); break; }
      const end = j + close.length;
      const inner = tpl.slice(i + (triple ? 3 : 2), j).trim();
      const parts = inner.split(/\s+/);
      const head = parts[0];
      if (head === '#each' || head === '#if' || head === '#unless') {
        const kind = head.slice(1), arg = parts[1];
        if (kind !== 'each' && isConfigBool(arg) && !data[arg]) {
          i = findMatchingClose(tpl, end, kind);   // toggled OFF → drop block
        } else { stack.push({ kind }); i = end; }
        continue;
      }
      if (head === '/each' || head === '/if' || head === '/unless') { stack.pop(); i = end; continue; }
      if (inner === 'else') {                      // drop the else branch
        const top = stack[stack.length - 1];
        if (top) { const after = findMatchingClose(tpl, end, top.kind); i = after - (`{{/${top.kind}}}`).length; }
        else i = end;
        continue;
      }
      // a content mustache → friendly, click-to-pick field chip
      const path = parts[parts.length - 1];
      const helper = parts.length > 1 ? parts.slice(0, -1).join(' ') : '';
      const k = units.length;
      units.push({ kind: 'token', start: i, end, triple, helper, path });
      const label = labelFor ? labelFor(helper, path) : path;
      out += `<span class="pbd-ph" data-k="${k}">${esc(label)}</span>`;
      i = end; continue;
    }
    if (litStart < 0) litStart = i;
    i++;
  }
  flushLit(i);
  out = out.replace(/\s+data-edit(-type)?="[^"]*"/g, '');   // edit hooks unused in this lens
  return { html: out, units };
}

function applyTemplateEdit(src, unit, text) {
  if (!unit) return src;
  if (unit.kind === 'lit') {
    const safe = String(text).trim().replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
    return src.slice(0, unit.start) + safe + src.slice(unit.end);
  }
  const path = String(text).trim();
  const inner = unit.helper ? unit.helper + ' ' + path : path;
  const mustache = unit.triple ? `{{{${inner}}}}` : `{{${inner}}}`;
  return src.slice(0, unit.start) + mustache + src.slice(unit.end);
}

function applyTemplateToken(src, unit, tag) {
  if (!unit) return src;
  return src.slice(0, unit.start) + tag + src.slice(unit.end);
}

export const PBTpl = {
  ITEM_FIELDS, itemFieldLabel, META_CATALOG, defaultColumns, defaultMeta,
  buildItemsTable, buildMetaStrip, spliceBlock, composeInvoice, resolveConfigBlocks, toPlaceholderView,
  toEditableTemplateView, applyTemplateEdit, applyTemplateToken, newId: c,
};

